import type { JSX } from 'react';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from 'src/app/pages/petitionDetails/pageContext';
import { ResponsiveBox } from 'src/app/pages/petitionDetails/shared/components';
import type { PetitionPhoto } from 'src/app/pages/petitionDetails/shared/types';
import { ImagePreload } from 'src/app/shared/components/optimization';
import { useQueryParamValue } from 'src/app/shared/hooks/location';

import { AddMediaContainer } from '../AddMedia';

import { Photo } from './components/Photo';
import { PhotoMobile } from './components/PhotoMobile';
import { PhotoProcessing } from './components/PhotoProcessing';

type Props = {
	photo?: PetitionPhoto;
};

type ImageUrls = {
	preloadUrl: [string] | [string, string];
	displayUrl: string;
	displayMobileUrl: string;
};

/**
 * Determines which images to preload and display based on PSF experiment flag.
 * When experiment is active: preloads share visual (for OG tags) but displays original photo (for users).
 * Otherwise: preloads and displays petition photo normally.
 */
function getImageUrls(
	photo: PetitionPhoto,
	shouldUseBestShareVisual: boolean,
	bestShareVisualUrl?: string | null,
): ImageUrls {
	if (shouldUseBestShareVisual && bestShareVisualUrl) {
		return {
			preloadUrl: [bestShareVisualUrl], // Social crawlers see this
			displayUrl: photo.url, // Users see original photo
			displayMobileUrl: photo.mobileUrl,
		};
	}

	return {
		preloadUrl: [photo.mobileUrl, photo.url], // Preload both variants
		displayUrl: photo.url,
		displayMobileUrl: photo.mobileUrl,
	};
}

export function MediaContainer({ photo }: Props): JSX.Element | null {
	const {
		data: { canEdit },
	} = usePetitionDetailsPageUserData();

	const {
		data: {
			petition: { bestShareVisual },
		},
	} = usePetitionDetailsPageData();

	// Check for best share visual flag
	const bestShareVisualParam = useQueryParamValue('best_share_visual');
	const shouldUseBestShareVisual = bestShareVisualParam === '1';

	if (photo) {
		if (photo.processing) {
			return <PhotoProcessing />;
		}

		const { preloadUrl, displayUrl, displayMobileUrl } = getImageUrls(
			photo,
			shouldUseBestShareVisual,
			bestShareVisual?.url,
		);

		return (
			<>
				<ImagePreload url={preloadUrl} fetchPriority="high" />

				<ResponsiveBox viewportSize="small">
					<PhotoMobile id="petition-photo-mobile" url={displayUrl} mobileUrl={displayMobileUrl} />
				</ResponsiveBox>

				<ResponsiveBox viewportSize="normal">
					<Photo id="petition-photo" url={displayUrl} mobileUrl={displayMobileUrl} />
				</ResponsiveBox>
			</>
		);
	}

	if (canEdit) {
		return <AddMediaContainer />;
	}

	return null;
}
